import React, { useMemo } from 'react'
import { OptimizelyExperiments } from 'constants/optimizely'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getPackageViewPricingPerNight } from 'lib/offer/getPackageViewPricingPerNightUtils'
import { OfferPackageOptionView, getCheapestSelectedPackageOptionWithDefault } from 'selectors/offerPage/offerPageSelectors'
import { getActivePackagesByRoom, PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { checkCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import _BaseHotelFlightsToggleButton from './_BaseHotelFlightsToggleButton'

interface Props {
  rooms?: Array<App.Occupants>;
  cheapestSelectedOption: OfferPackageOptionView;
  isPerNightPricingTestEnabled: boolean;
  multipleRoomPackages: PackageOptionViewByRoom;
  canRedeemLuxPlusBenefits: boolean;
}

function HotelFlightsToggleButton(props: Props) {
  const { rooms, cheapestSelectedOption, isPerNightPricingTestEnabled, multipleRoomPackages, canRedeemLuxPlusBenefits } = props

  const {
    flightsAvailable,
    pricing: hotelPrice,
    memberPricing: hotelMemberPrice,
    pricingWithFlights,
    memberPricingWithFlights,
  } = useMemo(() => isPerNightPricingTestEnabled ? getPackageViewPricingPerNight(cheapestSelectedOption) : cheapestSelectedOption, [cheapestSelectedOption, isPerNightPricingTestEnabled])

  const { multipleRoomHotelPrice, multipleRoomHotelMemberPrice, multipleRoomFlightPrice, multipleRoomMemberFlightPrice } = useMemo(() => (
    Object.values(multipleRoomPackages || {}).reduce((acc, pkg) => {
      if (!pkg) return acc

      const {
        hotelPrice,
        hotelMemberPrice,
        flightPrice,
        propertyFees = 0,
      } = isPerNightPricingTestEnabled ? getPackageViewPricingPerNight(pkg) : pkg

      return {
        multipleRoomHotelPrice: acc.multipleRoomHotelPrice + hotelPrice + propertyFees,
        multipleRoomHotelMemberPrice: acc.multipleRoomHotelMemberPrice + hotelMemberPrice + propertyFees,
        multipleRoomFlightPrice: acc.multipleRoomFlightPrice + hotelPrice + propertyFees + flightPrice,
        multipleRoomMemberFlightPrice: acc.multipleRoomMemberFlightPrice + hotelMemberPrice + propertyFees + flightPrice,
      }
    }, { multipleRoomHotelPrice: 0, multipleRoomHotelMemberPrice: 0, multipleRoomFlightPrice: 0, multipleRoomMemberFlightPrice: 0 })
  ), [isPerNightPricingTestEnabled, multipleRoomPackages])

  const hotelTotalPrice = useMemo(() => {
    if (canRedeemLuxPlusBenefits && hotelMemberPrice?.price) return (rooms?.length ?? 0) > 1 ? multipleRoomHotelMemberPrice : hotelMemberPrice.price
    return (rooms?.length ?? 0) > 1 ? multipleRoomHotelPrice : hotelPrice.price
  }, [canRedeemLuxPlusBenefits, hotelMemberPrice, rooms, multipleRoomHotelPrice, multipleRoomHotelMemberPrice, hotelPrice])

  const hotelFlightTotalPrice = useMemo(() => {
    if (canRedeemLuxPlusBenefits && memberPricingWithFlights?.price) return (rooms?.length ?? 0) > 1 ? multipleRoomMemberFlightPrice : memberPricingWithFlights.price
    return (rooms?.length ?? 0) > 1 ? multipleRoomFlightPrice : pricingWithFlights?.price ?? 0
  }, [canRedeemLuxPlusBenefits, memberPricingWithFlights, rooms?.length, multipleRoomMemberFlightPrice, multipleRoomFlightPrice, pricingWithFlights])

  return <_BaseHotelFlightsToggleButton
    flightsAvailable={flightsAvailable}
    isMemberPrice={!!hotelMemberPrice}
    hotelTotalPrice={hotelTotalPrice}
    hotelFlightTotalPrice={hotelFlightTotalPrice}
  />
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  cheapestSelectedOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  isPerNightPricingTestEnabled: !!getOptimizelyExperimentVariation(appState, OptimizelyExperiments.pricePerNight),
  multipleRoomPackages: getActivePackagesByRoom(state, appState),
  canRedeemLuxPlusBenefits: checkCanRedeemLuxPlusBenefits(appState),
}))(HotelFlightsToggleButton)
